import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Loader2, PackageOpen, Receipt, Search, Truck } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import SEOHead from "../components/SEOHead";
import { checkoutApi } from "../lib/api";
import { formatDate, formatRupiah } from "../lib/format";

const toneClass = {
  success: "bg-emerald-100 text-emerald-700 border-emerald-200",
  warning: "bg-amber-100 text-amber-700 border-amber-200",
  danger: "bg-rose-100 text-rose-700 border-rose-200",
  info: "bg-sky-100 text-sky-700 border-sky-200",
  purple: "bg-purple-100 text-purple-700 border-purple-200",
};

const statusMeta = {
  menunggu: { label: "Menunggu Pembayaran", tone: "warning" },
  dibayar: { label: "Dibayar", tone: "success" },
  diproses: { label: "Diproses", tone: "info" },
  dikirim: { label: "Dikirim", tone: "purple" },
  selesai: { label: "Selesai", tone: "success" },
  batal: { label: "Batal", tone: "danger" },
};

const PAGE_SIZE = 10;

const CheckoutHistoryPage = () => {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [orders, setOrders] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);

  const buildParams = (pageNumber = page) => {
    const value = query.trim();
    const params = {
      status_filter: status,
      skip: String((pageNumber - 1) * PAGE_SIZE),
      limit: String(PAGE_SIZE + 1),
    };

    if (/^inv[-\w]+$/i.test(value)) {
      params.invoice = value;
    } else {
      params.phone = value;
    }

    return params;
  };

  const searchOrders = async (pageNumber = 1) => {
    const value = query.trim();
    if (value.length < 6) {
      toast.error("Masukkan nomor HP atau invoice yang valid");
      return;
    }

    try {
      setLoading(true);
      setHasSearched(true);
      const data = await checkoutApi.searchOrders(buildParams(pageNumber));
      const rows = data || [];
      setOrders(rows.slice(0, PAGE_SIZE));
      setHasNext(rows.length > PAGE_SIZE);
      setPage(pageNumber);
      if (rows.length === 0) {
        toast.info("Riwayat pesanan tidak ditemukan");
      }
    } catch (error) {
      setOrders([]);
      setHasNext(false);
      toast.error(error.message || "Gagal mencari riwayat pesanan");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    searchOrders(1);
  };

  const totalPayment = (order) => order.total_payment || order.total;

  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8" data-testid="history-page">
      <SEOHead
        title="Riwayat Checkout"
        description="Halaman cek riwayat checkout dan status pesanan Teras Alami."
        path="/riwayat"
        noIndex
      />

      <div className="mb-8">
        <h1 className="font-display text-3xl font-semibold text-stone-900 sm:text-4xl">
          Riwayat Checkout
        </h1>
        <p className="mt-2 text-lg text-stone-600">
          Cek status pesanan menggunakan nomor HP checkout atau nomor invoice.
        </p>
      </div>

      <form onSubmit={onSubmit} className="mb-6 flex flex-col gap-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm sm:flex-row sm:p-5">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-400" />
          <Input
            className="h-12 rounded-xl pl-11"
            placeholder="Masukkan nomor HP atau invoice"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            data-testid="history-search"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-12 rounded-xl sm:w-56" data-testid="history-status-filter">
            <SelectValue placeholder="Semua status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="menunggu">Menunggu Pembayaran</SelectItem>
            <SelectItem value="diproses">Diproses</SelectItem>
            <SelectItem value="dikirim">Dikirim</SelectItem>
            <SelectItem value="selesai">Selesai</SelectItem>
            <SelectItem value="batal">Batal</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" disabled={loading} className="h-12 rounded-xl bg-emerald-600 px-6 text-white hover:bg-emerald-700">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
          Cari
        </Button>
      </form>

      {!hasSearched ? (
        <EmptyState
          title="Cari riwayat pesanan"
          description="Masukkan nomor HP yang dipakai saat checkout, atau masukkan nomor invoice untuk melihat satu pesanan."
        />
      ) : loading ? (
        <div className="rounded-2xl border border-stone-200 bg-white p-12 text-center text-stone-500">
          <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin" />
          Mencari riwayat pesanan...
        </div>
      ) : orders.length === 0 ? (
        <EmptyState
          title="Riwayat tidak ditemukan"
          description="Pastikan nomor HP atau invoice sama dengan data saat checkout."
        />
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm lg:block">
            <table className="w-full text-left">
              <thead className="bg-stone-50 text-sm text-stone-500">
                <tr>
                  <th className="px-5 py-4 font-medium">Invoice</th>
                  <th className="px-5 py-4 font-medium">Produk</th>
                  <th className="px-5 py-4 font-medium">Total</th>
                  <th className="px-5 py-4 font-medium">Pembayaran</th>
                  <th className="px-5 py-4 font-medium">Status Pesanan</th>
                  <th className="px-5 py-4 font-medium">Resi</th>
                  <th className="px-5 py-4 font-medium">Tanggal</th>
                  <th className="px-5 py-4 text-right font-medium">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {orders.map((order) => (
                  <tr key={order.invoice} data-testid={`row-order-${order.invoice}`}>
                    <td className="px-5 py-4 font-medium text-stone-900">{order.invoice}</td>
                    <td className="px-5 py-4 text-stone-700">{itemsLabel(order)}</td>
                    <td className="px-5 py-4 font-medium text-emerald-700">{formatRupiah(totalPayment(order))}</td>
                    <td className="px-5 py-4"><StatusBadge status={order.payment_status} /></td>
                    <td className="px-5 py-4"><StatusBadge status={order.order_status} /></td>
                    <td className="px-5 py-4 text-sm text-stone-600">{order.tracking_number || "-"}</td>
                    <td className="px-5 py-4 text-sm text-stone-500">{formatDate(order.created_at)}</td>
                    <td className="px-5 py-4 text-right">
                      <Button variant="outline" className="h-10 rounded-lg" onClick={() => setDetail(order)}>
                        <Eye className="mr-1 h-4 w-4" /> Detail
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-4 lg:hidden">
            {orders.map((order) => (
              <div key={order.invoice} className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm text-stone-500">{order.invoice}</p>
                    <p className="font-display text-lg font-semibold text-stone-900">{itemsLabel(order)}</p>
                  </div>
                  <StatusBadge status={order.order_status} />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <Info label="Total" value={formatRupiah(totalPayment(order))} strong />
                  <Info label="Pembayaran" value={<StatusBadge status={order.payment_status} />} />
                  <Info label="Resi" value={order.tracking_number || "-"} />
                  <Info label="Tanggal" value={formatDate(order.created_at)} />
                </div>
                <Button variant="outline" className="mt-4 h-12 w-full rounded-xl" onClick={() => setDetail(order)}>
                  <Eye className="mr-2 h-4 w-4" /> Lihat Detail
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-stone-500">Halaman {page}</p>
            <div className="flex gap-2">
              <Button variant="outline" className="h-10 rounded-xl" disabled={page <= 1 || loading} onClick={() => searchOrders(page - 1)}>
                Sebelumnya
              </Button>
              <Button variant="outline" className="h-10 rounded-xl" disabled={!hasNext || loading} onClick={() => searchOrders(page + 1)}>
                Berikutnya
              </Button>
            </div>
          </div>
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-2xl">
              <Receipt className="h-6 w-6 text-emerald-600" /> Detail Pesanan
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-5 pt-2">
              <div className="flex flex-wrap gap-2">
                <StatusBadge status={detail.payment_status} />
                <StatusBadge status={detail.order_status} />
              </div>
              <dl className="grid grid-cols-1 gap-4 text-base sm:grid-cols-2">
                <Info label="Invoice" value={detail.invoice} />
                <Info label="Tanggal" value={formatDate(detail.created_at)} />
                <Info label="Nama Pembeli" value={detail.customer.full_name} />
                <Info label="No. HP/WA" value={detail.customer.phone} />
                <Info
                  label="Alamat"
                  value={[
                    detail.customer.address,
                    detail.customer.kelurahan,
                    detail.customer.kecamatan,
                    detail.customer.kota,
                    detail.customer.province,
                    detail.customer.zip_code,
                  ].filter(Boolean).join(", ")}
                  wide
                />
              </dl>
              <div className="rounded-2xl border border-stone-200">
                <div className="border-b border-stone-200 px-4 py-3 font-medium text-stone-900">Item Produk</div>
                <div className="divide-y divide-stone-100">
                  {(detail.items || []).map((item) => (
                    <div key={item.id} className="flex justify-between gap-4 px-4 py-3 text-sm">
                      <span className="font-medium text-stone-900">{item.product_name_snapshot} x {item.quantity}</span>
                      <span className="text-stone-600">{formatRupiah(item.subtotal)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid gap-4 rounded-2xl bg-stone-50 p-4 text-sm sm:grid-cols-2">
                <Info label="Metode Bayar" value={detail.payment_method_name || detail.payment_method || "-"} />
                <Info label="Total Dibayar" value={formatRupiah(totalPayment(detail))} strong />
                <Info label="Kurir" value={[detail.courier_name, detail.courier_service, detail.courier_etd].filter(Boolean).join(" - ") || "-"} />
                <Info label="Nomor Resi" value={detail.tracking_number || "Belum tersedia"} />
              </div>
              <div className="flex flex-wrap gap-3">
                <Button asChild className="h-12 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700">
                  <Link to={detail.payment_status === "dibayar" ? `/sukses/${detail.invoice}` : `/payment/${detail.invoice}`}>
                    <Truck className="mr-2 h-4 w-4" /> Lihat Status Lengkap
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
};

const StatusBadge = ({ status }) => {
  const meta = statusMeta[status] || { label: status || "-", tone: "info" };
  return (
    <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${toneClass[meta.tone]}`}>
      {meta.label}
    </span>
  );
};

const Info = ({ label, value, wide = false, strong = false }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <dt className="text-sm text-stone-500">{label}</dt>
    <dd className={strong ? "font-semibold text-emerald-700" : "font-medium text-stone-900"}>{value || "-"}</dd>
  </div>
);

const EmptyState = ({ title, description }) => (
  <div className="rounded-2xl border border-stone-200 bg-white p-14 text-center">
    <PackageOpen className="mx-auto mb-3 h-16 w-16 text-stone-300" />
    <p className="font-display text-xl font-semibold text-stone-900">{title}</p>
    <p className="mt-1 text-stone-500">{description}</p>
    <Button asChild className="mt-5 h-12 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700">
      <Link to="/">Belanja Sekarang</Link>
    </Button>
  </div>
);

const itemsLabel = (order) => {
  const items = order.items || [];
  if (items.length === 0) return "-";
  if (items.length === 1) return `${items[0].product_name_snapshot} x ${items[0].quantity}`;
  return `${items[0].product_name_snapshot} +${items.length - 1} item lainnya`;
};

export default CheckoutHistoryPage;
